import { mockCheckoutData } from '@/mocks/fullPageCartMocks.js';
import { getPaymentMethods } from '@/utils/getConfiguration.js';
import { dayjs } from './dates/dates';
import {
  EXACT_DATE_TIMESLOT_TOKEN,
  PREDICTION_TIMESLOT_TOKEN,
  createPredictiveTimeslotTextWithDate,
} from './logic/timeslots';
import { NUMBER_THEN_STREET, STREET_THEN_NUMBER } from '@/constants/addressFormat';
const DELIVERY_LABELS = {
  easy: 'Delivery',
  express: 'Delivery',
  takeAway: 'Take away',
};

const defaultFormatter = new Intl.NumberFormat('el-GR', { currency: 'EUR', style: 'currency' });

export const formatOrder = (order, orderStatusMap, locale, timeFormat, addressFormat = NUMBER_THEN_STREET) => {
  if (!order || !orderStatusMap) return {};
  return {
    _id: order._id,
    date: dayjs(order.createdAt).tz().locale(locale).format(`dddd, ${process.env.dateFormat}`),
    orderId: `#${order.friendlyId}`,
    numberOfProducts: order.products?.length,
    price: `${order.totalPrice / 100}€`,
    shippingMethod: DELIVERY_LABELS[order.selectedService],
    timeStart: dayjs(order.timeSlotStart).tz().locale(locale).format(timeFormat),
    timeEnd: dayjs(order.timeSlotEnd).tz().locale(locale).format(timeFormat),
    address: formatOrderAddress(order, addressFormat),
    isEditable: order?.isEligibleForCancel || order?.isEligibleForEdit,
    ...(orderStatusMap && order.status && { badge: orderStatusMap[order.status] }),
  };
};

export const createOrderStatusMap = config => {
  if (!config) return {};

  const { statuses } = config?.order?.processFlow;

  const statusMap = {};
  for (const statusContainer of statuses) {
    statusMap[statusContainer.status] = { text: '', emoji: '🤖', type: 'warning' };
    statusMap[statusContainer.status].text = statusContainer.clientTitle;

    switch (statusContainer.status) {
      case 'ACCEPTED':
        statusMap[statusContainer.status].emoji = '🤩';
        break;
      case 'COMPLETED':
        statusMap[statusContainer.status].emoji = '🎉';
        statusMap[statusContainer.status].type = 'success';
        break;
      case 'CANCELLED':
        statusMap[statusContainer.status].emoji = '😵';
        statusMap[statusContainer.status].type = 'error';
        break;
      case 'UNDER_DELIVERY':
        statusMap[statusContainer.status].emoji = '🚚';
        statusMap[statusContainer.status].type = 'success';
        break;
      case 'PENDING' || 'PAYMENT_PENDING':
        statusMap[statusContainer.status].emoji = '⌛';
        break;
      case 'UNDER_PICKING':
        statusMap[statusContainer.status].emoji = '🛒';
        statusMap[statusContainer.status].type = 'success';
        break;
      case 'INVOICING':
        statusMap[statusContainer.status].emoji = '📄';
        statusMap[statusContainer.status].type = 'success';
        break;
      case 'PICKUP_READY':
        statusMap[statusContainer.status].emoji = '📦';
        statusMap[statusContainer.status].type = 'success';
        break;
    }
  }

  return statusMap;
};

export const formatOrderAddress = (order, addressFormat = NUMBER_THEN_STREET) => {
  if (order.deliveryService === 'takeAway') {
    return order?.shop?.name;
  }

  if (order?.address?.street && order?.address?.streetNo && order?.address?.city) {
    if (addressFormat === NUMBER_THEN_STREET)
      return `${order?.address?.streetNo} ${order?.address?.street}, ${order?.address?.postalCode}, ${order?.address?.city}`;
    if (addressFormat === STREET_THEN_NUMBER)
      return `${order?.address?.street} ${order?.address?.streetNo}, ${order?.address?.postalCode}, ${order?.address?.city}`;
  }

  return order?.shop?.name;
};

export const getOrderDetails = (
  order,
  deliveryServices,
  badgesTokens,
  daysWordings,
  locale,
  predictiveTimeTitle,
  formatter = defaultFormatter,
  timeFormat,
  addressFormat = NUMBER_THEN_STREET
) => {
  const selectedService = deliveryServices ? deliveryServices?.find?.(i => i.id === order?.deliveryService) : {};
  const paymentDropdownOptions = getPaymentMethods('', selectedService) ?? mockCheckoutData.methods;
  let timeTitle, timeSubtitle;
  const { timeslotsInfo } = order;

  if (timeslotsInfo?.type === PREDICTION_TIMESLOT_TOKEN) {
    const predictions = {
      ...timeslotsInfo.prediction,
      timeslotStart: order.timeSlotStart,
      timeslotEnd: order.timeSlotEnd,
    };

    timeSubtitle = createPredictiveTimeslotTextWithDate(predictions, undefined, locale || 'el')[1];
    timeTitle = predictiveTimeTitle || 'Παράδοση';
  } else {
    timeSubtitle =
      getDeliveryTime(order?.timeSlotStart, timeFormat) + ' - ' + getDeliveryTime(order?.timeSlotEnd, timeFormat);
    timeTitle = getDeliveryDate(order?.timeSlotEnd, daysWordings);
  }

  const selectedPayment = paymentDropdownOptions
    ? paymentDropdownOptions?.find?.(i => i.type === order?.paymentType)
    : {};

  let orderAddress = '';
  if (order?.address?.street && selectedService?.deliversToAddress) {
    if (addressFormat === NUMBER_THEN_STREET) orderAddress = `${order?.address?.streetNo} ${order?.address?.street}`;
    if (addressFormat === STREET_THEN_NUMBER) orderAddress = `${order?.address?.street} ${order?.address?.streetNo}`;
  } else {
    orderAddress = order?.shop?.name || ' - ';
  }

  return [
    {
      status: 'Delivery',
      title: selectedService?.title,
      subTitle: orderAddress,
    },
    {
      status: 'Time',
      title: timeTitle,
      subTitle: timeSubtitle,
    },
    {
      status: 'Payment',
      title: badgesTokens ? badgesTokens.paymentMethodToken : 'Πληρωμή με',
      subTitle: selectedPayment?.name,
    },
    {
      status: 'Order',
      title: formatter.format(order?.totalPrice / 100),
      subTitle: `${order?.products?.length}  ${badgesTokens ? ' ' + badgesTokens.productsToken : ' Προϊόντα'}`,
    },
  ];
};

export const createOrderDetails = (
  order,
  deliveryServices,
  badgesTokens,
  daysWordings = ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'],
  locale,
  predictiveTimeTitle,
  formatter = defaultFormatter,
  timeFormat,
  addressFormat = NUMBER_THEN_STREET
) => {
  if (_.isEmpty(order) || order?.data.length === 0) return [[]];

  return order?.data.map(o =>
    getOrderDetails(
      o,
      deliveryServices,
      badgesTokens,
      daysWordings,
      locale,
      predictiveTimeTitle,
      formatter,
      timeFormat,
      addressFormat
    )
  );
};

export const getDeliveryDate = (dateString, daysWordings) => {
  const date = new Date(dateString);

  const daysOfWeek = ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'];
  const dayOfWeek = daysWordings[date.getDay()];

  const day = ('0' + date.getDate()).slice(-2);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);

  return `${dayOfWeek} ${day}/${month}`;
};
export const getDeliveryTime = (dateString, timeFormat) => {
  return dayjs(dateString).tz().format(timeFormat);
};
